import { DatePicker } from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import { getAttendanceByClass, getAttendanceByStudent, getAttendanceSummary } from "../../api/statistics.api";
import Button from "../../components/Button";
import ErrorResponse from "../../components/ErrorResponse";
import SimpleHeader from "../../components/layout/SimpleHeader";
import Loader from "../../components/Loader";
import ByClass from "../../components/statistics/attendance/ByClass";
import ByStudent from "../../components/statistics/attendance/ByStudent";
import Summary from "../../components/statistics/attendance/Summary";
import MultiCampusModal from "../../components/statistics/regular-test/modals/MultiCampusModal";
import TabItem from "../../components/statistics/TabItem";
import useAxios from "../../hooks/useAxios";
import useNotification from "../../hooks/useNotification";
import config from "../../lib/config";
import { IAttendanceParams, IGetAttendanceByClass, IGetAttendanceByStudent, IGetAttendanceSummary } from "../../models/statistics.model";
import { RootState } from "../../store/rootReducer";
import StatDatePickerTable from "../../components/statistics/DatePickerTable";
import { DateTableType } from "../../constans";
import TableEmpty from "../../components/common/TableEmpty";
import SelectCampusMultiChoice from "../../components/SelectCampusMultiChoice";

enum Contents {
  Summary,
  Class,
  Student,
}

const StatisticsAttendance = () => {
  const { t } = useTranslation();
  const { auth } = useSelector((state: RootState) => state.auth);
  const { infoNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetAttendanceSummary|IGetAttendanceByClass|IGetAttendanceByStudent>();
  const { serverDate } = useSelector((state: RootState) => state.serverDate);

  const [isFirstRun, setIsFirstRun] = useState(true);
  const [srtDate, setSrtDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [campusCodes, setCampusCodes] = useState<string[]>([]);
  const [attendStatus, setAttendStatus] = useState<string>();
  const [pageNo, setPageNo] = useState(1);
  const [content, setContent] = useState(Contents.Summary);
  const [multiCampusVisible, setMultiCampusVisible] = useState(false);
  const [defSelType, setDefSelType] = useState<number>(0);

  useEffect(() => {
    if (isFirstRun) {
      setIsFirstRun(false);
      return;
    }

    getAttendanceData();
  }, [pageNo, attendStatus]);

  useEffect(() => {
    if (serverDate) {
      setSrtDate(moment(serverDate).format('YYYYMM01'));
      setEndDate(moment(serverDate).format('YYYYMMDD'));
    }
  }, [serverDate]);

  useEffect(() => {
    if (isFirstRun) {
      return;
    }

    getAttendanceData(1);
  }, [content]);

  const getAttendanceData = (_pageNo?: number, _attendStatus?: string) => {
    if (!srtDate) {
      return infoNotification(t('statistics.attendance.message1'));
    }
    if (!endDate) {
      return infoNotification(t('statistics.attendance.message2'));
    }
    if (_pageNo) {
      setPageNo(_pageNo);
    }

    const req: IAttendanceParams = {
      srtDate,
      endDate,
      campusCode: auth.hqYn === 'Y'
        ? campusCodes
        : (campusCodes.length ? campusCodes : auth.campus),
      pageNo: _pageNo || pageNo,
      perPage: config.pageSize,
    };
    if (_attendStatus || attendStatus) {
      req.attendStatus = _attendStatus || attendStatus;
    }

    switch (content) {
      case Contents.Summary:
        execute(getAttendanceSummary(req));
        break;
      case Contents.Class:
        execute(getAttendanceByClass(req));
        break;
      case Contents.Student:
        execute(getAttendanceByStudent(req));
        break;
    }
  }

  const renderContent = () => {
    switch (content) {
      case Contents.Summary:
        return (
          <Summary
            // @ts-ignore 
            rawData={res?.data}
            total={res?.total}
            isAllCampus={campusCodes.length === 0}
            pageNo={pageNo}
            setPageNo={setPageNo}
            srtDate={srtDate}
            endDate={endDate}
            campusCodes={campusCodes}
          />
        );
      case Contents.Class:
        return (
          <ByClass
            // @ts-ignore 
            data={res?.data}
            total={res?.total}
            pageNo={pageNo}
            setPageNo={setPageNo}
            srtDate={srtDate}
            endDate={endDate}
            campusCodes={campusCodes}
          />
        );
      case Contents.Student:
        return (
          <ByStudent
            // @ts-ignore 
            data={res?.data}
            total={res?.total}
            pageNo={pageNo}
            setPageNo={setPageNo}
            srtDate={srtDate}
            endDate={endDate}
            campusCodes={campusCodes}
            attendStatus={attendStatus}
            setAttendStatus={setAttendStatus}
          />
        );
    }
  }

  return (
    <>
    <SimpleHeader
      iconFile="icon-statistics-big.svg"
      parentMenu={t('common.menu.statistics')}
      currentMenu={t('statistics.attendance.title1')}
      onlyTitle={true}
    />
    
    <div className="common-search">
      <div className="search-area">
        <ul className="search-bx">
          <li>
            <label>{t('class.class.placeholder1')}</label> {/* 캠퍼스 */}
            <SelectCampusMultiChoice
              placeholder={t('statistics.eLearning.title2')}
              onChange={setCampusCodes}
              value={campusCodes}
            />
            <label>
              ({campusCodes.length ? `${campusCodes.length}${t('statistics.attendance.text1')}` : t('common.text.all')})
            </label>
          </li>
          <li className="period">
            <label>{t('sms.list.column1')}</label> {/* 기간 */}
            <div className="d-flex flex-column">
              <div className="d-flex align-items-center">  
                <div className="date-picker__area">
                  <DatePicker
                    value={srtDate ? moment(srtDate) : undefined}
                    onChange={e => {
                      if (e === null) setSrtDate('');
                      else setSrtDate(moment(e).format('YYYYMMDD'))
                      setDefSelType(0);
                    }}
                    format={['YYYY-MM-DD', 'YYYYMMDD']}
                  />
                  ~
                  <DatePicker
                    value={endDate ? moment(endDate) : undefined}
                    onChange={e => {
                      if (e === null) setEndDate('');
                      else setEndDate(moment(e).format('YYYYMMDD'))
                      setDefSelType(0);
                    }}
                    format={['YYYY-MM-DD', 'YYYYMMDD']}
                  />
                </div>
                <div className="comm-btn__area">
                  <Button
                    className="btn-basic small black"
                    onClick={() => getAttendanceData(1)}
                    text={t('common.button.search')}
                  />
                </div>
              </div>
              <div className="comm-btn__area mt-2">
                <StatDatePickerTable
                  setStartDate={setSrtDate}
                  setEndDate={setEndDate}
                  type={DateTableType.Simple}
                  defSelType={defSelType}
                  setDefSelType={setDefSelType}
                />
              </div>
            </div>
          </li>
        </ul>
      </div>
    </div>	

    <div className="content scrollbar">
      {isLoading && <Loader />}
      <ErrorResponse error={error} />
      {
        res?.data
          ? (
            <>
              <div className="comm-tab__line">
                <ul>
                  <TabItem
                    currentContent={content}
                    onSetContent={setContent}
                    tabContent={Contents.Summary}
                    tabLabel={t('statistics.attendance.Tab1')}
                  />
                  <TabItem
                    currentContent={content}
                    onSetContent={setContent}
                    tabContent={Contents.Class}
                    tabLabel={t('common.menu.class')}
                  />
                  <TabItem
                    currentContent={content}
                    onSetContent={setContent}
                    tabContent={Contents.Student}
                    tabLabel={t('common.menu.student')}
                  />
                </ul>
              </div>
              <div>
                {renderContent()}
              </div>
            </>
            )
          : isLoading
            ? null
            : (
              <TableEmpty text={t('statistics.appday.text1')} />
              )
      }
    </div>

    {multiCampusVisible &&
      <MultiCampusModal
        visible={multiCampusVisible}
        setVisible={setMultiCampusVisible}
        campusCodes={campusCodes}
        setCampusCodes={setCampusCodes}
      />
    }      
    </>
  );
}

export default StatisticsAttendance;
